import { ImageFromServer } from '@/components/images/ImageFromServer';
import { ExerciseDTO, SupportExerciseDTO } from '@/generated';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { findRelatedExercises } from '../exercise-utils';

type CopySupportExercisesModalProps = {
  show: boolean;
  progressionExerciseIds: string[];
  regressionExerciseIds: string[];
  exercises: ExerciseDTO[];
  onHide: () => void;
  onCopy: (supportExercises: SupportExerciseDTO[]) => void;
};

export const CopySupportExercisesModal = (props: CopySupportExercisesModalProps) => {
  const [sourceExerciseId, setSourceExerciseId] = useState<string>();
  const [selectedSupportExerciseIds, setSelectedSupportExerciseIds] = useState<string[]>([]);
  const sourceExercises = useMemo(
    () => findRelatedExercises(props.progressionExerciseIds, props.regressionExerciseIds, props.exercises),
    [props.progressionExerciseIds, props.regressionExerciseIds, props.exercises],
  );
  const sourceExercise = sourceExercises.find((exercise) => exercise.id === sourceExerciseId);

  useEffect(() => {
    if (props.show) {
      setSourceExerciseId(undefined);
      setSelectedSupportExerciseIds([]);
    }
  }, [props.show]);

  const selectSourceExercise = (exercise: ExerciseDTO) => {
    setSourceExerciseId(exercise.id);
    setSelectedSupportExerciseIds(exercise.supportExercises.map((supportExercise) => supportExercise.exerciseId));
  };

  const toggleSupportExercise = (exerciseId: string) => {
    setSelectedSupportExerciseIds((current) => (current.includes(exerciseId) ? current.filter((selectedId) => selectedId !== exerciseId) : [...current, exerciseId]));
  };

  return (
    <Dialog open={props.show} onClose={props.onHide} fullWidth maxWidth="md">
      <DialogTitle>Copy support exercises</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) minmax(260px, 1.4fr)' }, gap: 3, minHeight: 300 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Progressions and regressions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1 }}>
              {sourceExercises.map((exercise) => (
                <Paper
                  key={exercise.id}
                  component="button"
                  type="button"
                  aria-pressed={sourceExerciseId === exercise.id}
                  onClick={() => selectSourceExercise(exercise)}
                  variant="outlined"
                  sx={{
                    borderColor: sourceExerciseId === exercise.id ? 'common.white' : 'divider',
                    bgcolor: sourceExerciseId === exercise.id ? 'action.selected' : 'background.paper',
                    color: 'text.primary',
                    cursor: 'pointer',
                    p: 1,
                    minWidth: 0,
                  }}
                >
                  <ImageFromServer id={exercise.imageId} thumbnail alt="" sx={{ width: 72, height: 72, mx: 'auto', borderRadius: 1 }} />
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {exercise.name}
                  </Typography>
                </Paper>
              ))}
            </Box>
            {!sourceExercises.length && <Typography color="text.secondary">No connected progressions or regressions found.</Typography>}
          </Box>

          <Box sx={{ borderLeft: { sm: 1 }, borderColor: { sm: 'divider' }, pl: { sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {sourceExercise ? `Support exercises of ${sourceExercise.name}` : 'Support exercises'}
            </Typography>
            {!sourceExercise && <Typography color="text.secondary">Select an exercise on the left.</Typography>}
            {sourceExercise && !sourceExercise.supportExercises.length && <Typography color="text.secondary">This exercise has no support exercises.</Typography>}
            <Stack direction="row" gap={1} flexWrap="wrap">
              {sourceExercise?.supportExercises.map((supportExercise) => {
                const supportExerciseName = props.exercises.find((exercise) => exercise.id === supportExercise.exerciseId)?.name ?? supportExercise.exerciseId;
                const selected = selectedSupportExerciseIds.includes(supportExercise.exerciseId);
                return (
                  <Chip
                    key={supportExercise.exerciseId}
                    label={supportExerciseName}
                    title={supportExercise.description || 'No description'}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleSupportExercise(supportExercise.exerciseId)}
                  />
                );
              })}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onHide}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!sourceExercise || !selectedSupportExerciseIds.length}
          onClick={() => {
            if (!sourceExercise) return;
            props.onCopy(sourceExercise.supportExercises.filter((supportExercise) => selectedSupportExerciseIds.includes(supportExercise.exerciseId)));
          }}
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};
